import { useEffect, useMemo, useState } from "react";
import { Button, Input, Segmented, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ClearOutlined,
  LinkOutlined,
  OpenAIOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import apiClient from "../../actions/api";
import isEmpty from "../../validations/is-empty";
import openNotification from "../../components/Notifications/openNotification";
import { saveBid } from "../../actions/bid";

// import style
import "../../style/bid/bid.scss";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const { TextArea } = Input;

const Bid = ({ title }) => {
  // set title
  document.title = title;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.auth.user);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const draftID = queryParams.get("draftID");
  const bidID = queryParams.get("bidID");

  const [jobDesc, setjobDesc] = useState(""); // set job description value
  const [jobUrl, setJobUrl] = useState(""); // set job url value
  const [status, setStatus] = useState(true);
  const [urlStatus, setUrlStatus] = useState(true);
  const [loading, setLoading] = useState(false);
  const [autoFlag, setAutoFlag] = useState(false);
  const [sourceType, setSourceType] = useState("resume_document");
  const [resumeProfileId, setResumeProfileId] = useState("");
  const resumeProfiles = useMemo(
    () =>
      user?.resume_documents?.length > 0
        ? user.resume_documents
        : user?.resume_document_url
          ? [{ _id: "legacy", name: "Existing Resume" }]
          : [],
    [user?.resume_document_url, user?.resume_documents],
  );
  const templatesReady =
    !isEmpty(user?.template_url) && !isEmpty(user?.template_coverletter_url);

  useEffect(() => {
    if (!resumeProfileId && resumeProfiles.length > 0) {
      setResumeProfileId(resumeProfiles[0]._id);
    }
  }, [resumeProfileId, resumeProfiles]);

  useEffect(() => {
    if (isEmpty(user) || templatesReady || user?.resume_document_url) return;

    openNotification(
      "error",
      "Document templates required",
      "Please upload both a resume template and a cover letter template before building a resume.",
    );
    navigate("/profile", { replace: true });
  }, [navigate, templatesReady, user]);

  useEffect(() => {
    if (!templatesReady && !user?.resume_document_url) return;

    if (!isEmpty(user) && !isEmpty(bidID)) {
      const params = {
        id: bidID,
      };

      apiClient
        .post("/bids/get-bid-item", params)
        .then((res) => {
          if (!isEmpty(res.data.result)) {
            if (res.data.flag) {
              setJobUrl(res.data.result.job_url);
              setjobDesc(res.data.result.job_desc);
              setAutoFlag(true);
            }
          }
        })
        .catch((err) => {
          if (!isEmpty(err?.response?.data?.errors)) {
            err.response.data.errors.forEach((item) => {
              openNotification("error", "Validation Error!", item.msg);
            });
          }
        });
    }

    if (!isEmpty(user) && !isEmpty(draftID)) {
      const params = {
        draftID: draftID,
      };

      apiClient
        .post("/bids/check-draft-by-id", params)
        .then((res) => {
          if (!isEmpty(res.data)) {
            if (res.data.flag) {
              setJobUrl(res.data.draft.url);
              setjobDesc(res.data.draft.content);
              setAutoFlag(true);
            }
          }
        })
        .catch((err) => {
          if (!isEmpty(err?.response?.data?.errors)) {
            err.response.data.errors.forEach((item) => {
              openNotification("error", "Validation Error!", item.msg);
            });
          }
        });
    }
  }, [user, templatesReady]);

  useEffect(() => {
    setLoading(true);

    if (jobDesc.trim().length !== 0) {
      setStatus(true);
    }

    if (jobUrl.trim().length !== 0) {
      setUrlStatus(true);
    }

    setLoading(false);

    if (jobDesc.trim().length > 0 && jobUrl.trim().length > 0 && autoFlag) {
      handleGenerateDraft();
    }
  }, [jobDesc, jobUrl]);

  const handleClear = () => {
    setjobDesc("");
    setStatus(false);
    setJobUrl("");
    setUrlStatus(false);
  };

  const handleGenerateDraft = async () => {
    if (jobUrl.trim().length === 0) {
      setUrlStatus(false);
      return;
    }

    if (jobDesc.trim().length === 0) {
      setStatus(false);
      return;
    }

    if (sourceType === "template" && isEmpty(user.template_url)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please upload resume template.",
      );
      return;
    }
    if (sourceType === "resume_document" && !resumeProfileId) {
      openNotification(
        "error",
        "Resume document required",
        "Please upload your resume document in View Profile first.",
      );
      return;
    }

    const params = {
      user: user._id,
      job_url: jobUrl.trim(),
      job_desc: jobDesc.trim(),
      source_type: sourceType,
      resume_profile_id:
        sourceType === "resume_document" ? resumeProfileId : undefined,
    };

    setLoading(true);
    await apiClient
      .post("/bids/gen-draft", params)
      .then((res) => {
        if (!isEmpty(res.data)) {
          dispatch(saveBid(res.data));
          navigate("/resume-preview");
          openNotification(
            "success",
            "Success",
            "You've generated Draft for resume.",
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  return (
    <div className="bidSection">
      <div className="bid-heading">
        <div>
          <h2 className="title">
            <span className="bid-title-icon">
              <OpenAIOutlined />
            </span>
            Build resume
          </h2>
          <p>Turn a job description into a tailored resume draft.</p>
        </div>
      </div>

      <div className="bid-action-top">
        <Button
          type="primary"
          htmlType="button"
          onClick={handleGenerateDraft}
          loading={loading}
          icon={<RocketOutlined />}
        >
          Generate Resume Draft
        </Button>
      </div>

      <div className="bid-form-card">
        <div className="bid-source-selector">
          <span>Resume source</span>
          <Segmented
            className="resume-source-segmented"
            value={sourceType}
            onChange={setSourceType}
            options={[
              { label: "Templates", value: "template" },
              { label: "Profiles", value: "resume_document" },
            ]}
          />
        </div>
        {sourceType === "resume_document" && (
          <div className="bid-field resume-profile-selector">
            <label className="label">Resume profile</label>
            <Select
              size="large"
              value={resumeProfileId || undefined}
              onChange={setResumeProfileId}
              placeholder="Choose a resume profile"
              options={resumeProfiles.map((profile) => ({
                label: profile.name,
                value: profile._id,
              }))}
            />
          </div>
        )}
        <div className="bid-field">
          <label className="label">
            Job URL&nbsp;<span>*</span>
          </label>
          <Input
            prefix={<LinkOutlined />}
            status={urlStatus ? "" : "error"}
            rows={8}
            placeholder="Paste the job url here"
            value={jobUrl}
            onChange={(e) => setJobUrl(e.target.value)}
          />
        </div>

        <div className="bid-field">
          <label className="label">
            Job Description&nbsp;<span>*</span>
          </label>
          <TextArea
            status={status ? "" : "error"}
            rows={16}
            placeholder="Paste the job description here"
            value={jobDesc}
            onChange={(e) => setjobDesc(e.target.value)}
          />
        </div>

        <div className="bid-action">
          <Button
            type="primary"
            htmlType="button"
            onClick={handleGenerateDraft}
            loading={loading}
            icon={<RocketOutlined />}
          >
            Generate Resume Draft
          </Button>
          <Button
            type="default"
            htmlType="button"
            onClick={handleClear}
            loading={loading}
            icon={<ClearOutlined />}
          >
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Bid;
